/**
 * Tenant-configurable OTLP collector port (O-1709).
 *
 * Endpoints run an on-host OTLP collector on a loopback port; corporate
 * OTel collectors (Datadog agent, Grafana Alloy, Dynatrace) default to the
 * same OTLP/HTTP convention port 4318, so tenants can move Origin's
 * collector out of the way fleet-wide. Unset means "use the default".
 *
 * The accepted range mirrors the api-server validation (and the agent's own
 * decode-boundary check): 1024–65535, loopback-only by construction on the
 * endpoint — only the port is configurable, never the interface.
 */

export const DEFAULT_OTEL_COLLECTOR_PORT = 4318;
export const MIN_OTEL_COLLECTOR_PORT = 1024;
export const MAX_OTEL_COLLECTOR_PORT = 65535;

/**
 * Parse the Settings-page text input for the OTLP collector port.
 *
 * Returns `{ ok: true, value }` where `value` is a number in range, or
 * `null` for an empty input (= unset, use the default port); or
 * `{ ok: false, error }` with a human-readable message when the text must
 * block the save.
 */
export function parseOtelCollectorPortInput(text) {
    const trimmed = String(text ?? "").trim();
    if (trimmed === "") return { ok: true, value: null };
    if (!/^\d+$/.test(trimmed)) {
        return { ok: false, error: "Port must be a whole number." };
    }
    const port = Number(trimmed);
    if (port < MIN_OTEL_COLLECTOR_PORT || port > MAX_OTEL_COLLECTOR_PORT) {
        return {
            ok: false,
            error: `Port must be between ${MIN_OTEL_COLLECTOR_PORT} and ${MAX_OTEL_COLLECTOR_PORT}.`,
        };
    }
    return { ok: true, value: port };
}

/**
 * Read the OTLP collector port off a GET /v1/tenant/configuration
 * `configuration` object, tolerating either field casing the gateway emits.
 * `null` = unset (default port).
 */
export function readOtelCollectorPort(cfg) {
    return cfg?.otelCollectorPort ?? cfg?.otel_collector_port ?? null;
}
